import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';

interface RecordingDetailProps {
  recording: any;
  onClose: () => void;
}

interface TranscriptSegment {
  speaker: string;
  startTime: number;
  text: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AVAILABLE_FIELDS: { token: string; label: string; example: string }[] = [
  { token: '{phone}', label: 'Phone (10 digits)', example: '1234567890' },
  { token: '{phone_formatted}', label: 'Phone (formatted)', example: '(123) 456-7890' },
  { token: '{agent}', label: 'Agent ID', example: 'rditsch' },
  { token: '{agent_email}', label: 'Agent Email', example: 'rditsch@woxomhealth.com' },
  { token: '{date}', label: 'Date (YYYY-MM-DD)', example: '2026-03-27' },
  { token: '{date_short}', label: 'Date (MMDDYYYY)', example: '03272026' },
  { token: '{time_12h}', label: 'Time (12h)', example: '02_30_45 PM' },
  { token: '{time_24h}', label: 'Time (24h)', example: '14_30_45' },
  { token: '{direction}', label: 'Direction', example: 'in' },
  { token: '{system}', label: 'System', example: 'pulsedial' },
  { token: '{callid}', label: 'Call ID', example: 'CA1234abcd' },
  { token: '{duration}', label: 'Duration (seconds)', example: '325' },
  { token: '{ext}', label: 'File Extension', example: 'mp3' },
  { token: '{original}', label: 'Original Filename', example: 'recording.mp3' },
];

function extractPhoneDigits(r: any): string {
  const fname = r.originalFilename || '';
  const match = fname.match(/\+?(\d{10,15})/);
  if (match) return match[1].length === 11 && match[1][0] === '1' ? match[1].slice(1) : match[1];
  return r.callerId && r.callerId !== '0000' ? r.callerId : '0000000000';
}

function extractTimestamp12h(r: any): string {
  const fname = r.originalFilename || '';
  const match = fname.match(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
  if (match) {
    let h = parseInt(match[4]);
    const m = match[5];
    const s = match[6];
    const ampm = h >= 12 ? 'PM' : 'AM';
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return `${h.toString().padStart(2, '0')}_${m}_${s} ${ampm}`;
  }
  if (r.ingestTimestamp) {
    try {
      const d = new Date(r.ingestTimestamp);
      let h = d.getHours();
      const ampm = h >= 12 ? 'PM' : 'AM';
      if (h > 12) h -= 12;
      if (h === 0) h = 12;
      return `${h.toString().padStart(2, '0')}_${d.getMinutes().toString().padStart(2, '0')}_${d.getSeconds().toString().padStart(2, '0')} ${ampm}`;
    } catch { /* fall through */ }
  }
  return '12_00_00 PM';
}

// Extension of the file actually stored (and therefore downloaded). The
// vendor's originalFilename can lie about it — DialedIn arrives as .wav but
// is transcoded to .mp3 at ingest.
function storedExtension(r: any): string {
  const storedName = (r.archivePath || r.normalizedFilename || '').split('/').pop() || '';
  const m = storedName.match(/\.([A-Za-z0-9]+)$/);
  if (m) return m[1].toLowerCase();
  return (r.originalFilename || '').split('.').pop() || 'mp3';
}

function resolvePattern(pattern: string, r: any): string {
  const phone = extractPhoneDigits(r);
  const ext = storedExtension(r);
  // {original} keeps the vendor's filename but with the real stored extension,
  // so downloaded bytes and extension always agree.
  const originalName = (r.originalFilename || 'recording.mp3').replace(/\.[^.]+$/, '') + `.${ext}`;
  const date = r.recordDate || 'unknown';
  const ts12 = extractTimestamp12h(r);
  const ts24 = (() => {
    const fname = r.originalFilename || '';
    const match = fname.match(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
    if (match) return `${match[4]}_${match[5]}_${match[6]}`;
    return '00_00_00';
  })();

  return pattern
    .replace(/\{phone\}/g, phone)
    .replace(/\{phone_formatted\}/g, phone.length === 10 ? `(${phone.slice(0,3)}) ${phone.slice(3,6)}-${phone.slice(6)}` : phone)
    .replace(/\{agent\}/g, r.agentId || 'unknown')
    .replace(/\{agent_email\}/g, r.agentEmail || (r.agentId ? `${r.agentId}@woxomhealth.com` : 'agent@woxomhealth.com'))
    .replace(/\{date\}/g, date)
    .replace(/\{date_short\}/g, date !== 'unknown' ? `${date.slice(5,7)}${date.slice(8,10)}${date.slice(0,4)}` : 'unknown')
    .replace(/\{time_12h\}/g, ts12)
    .replace(/\{time_24h\}/g, ts24)
    .replace(/\{direction\}/g, r.direction || 'unk')
    .replace(/\{system\}/g, r.systemId || 'unknown')
    .replace(/\{callid\}/g, r.callSid || r.recordingId || 'unknown')
    .replace(/\{duration\}/g, r.duration?.toString() || '0')
    .replace(/\{ext\}/g, ext)
    .replace(/\{original\}/g, originalName);
}

function RecordingDetail({ recording, onClose }: RecordingDetailProps) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<{ fullText: string; segments: TranscriptSegment[]; speakerCount: number; duration?: number; noSpeech?: boolean } | null>(null);
  const [transcriptStatus, setTranscriptStatus] = useState<string>('idle');
  const [, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savedFormats, setSavedFormats] = useState<{ id: string; name: string; pattern: string }[]>([]);
  const [restoreStatus, setRestoreStatus] = useState<string | null>(null);
  const [restoreMessage, setRestoreMessage] = useState('');
  const audioRef = useRef<HTMLAudioElement>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const getS3Key = useCallback(() => {
    if (recording.archivePath) {
      const match = recording.archivePath.match(/\/volume1\/archive\/([^/]+)\/archive\/(.+)/);
      if (match) return `${match[1]}/${match[2]}`;
    }
    return `${recording.systemId}/${recording.normalizedFilename || recording.originalFilename}`;
  }, [recording]);

  // Load saved export formats
  useEffect(() => {
    api.listExportFormats()
      .then((data: any) => setSavedFormats(data.formats || []))
      .catch(() => {});
  }, []);

  // Load audio URL
  useEffect(() => {
    const s3Key = getS3Key();
    api.getPlaybackUrl(s3Key)
      .then(data => setAudioUrl(data.url))
      .catch(() => setAudioUrl(null));
  }, [getS3Key]);

  // Check for existing transcript
  useEffect(() => {
    const s3Key = getS3Key();
    api.getTranscriptResult(s3Key)
      .then(data => {
        if (data.status === 'COMPLETED' && data.transcript) {
          setTranscript(data.transcript);
          setTranscriptStatus('completed');
        }
      })
      .catch(() => { /* No transcript yet */ });
  }, [getS3Key]);

  // Poll for transcription completion
  useEffect(() => {
    if (transcriptStatus === 'in_progress') {
      const s3Key = getS3Key();
      pollRef.current = setInterval(async () => {
        try {
          const status = await api.getTranscriptionStatus(s3Key);
          if (status.status === 'COMPLETED') {
            clearInterval(pollRef.current!);
            const s3Key = getS3Key();
            const result = await api.getTranscriptResult(s3Key);
            if (result.transcript) {
              setTranscript(result.transcript);
              setTranscriptStatus('completed');
            }
          } else if (status.status === 'FAILED') {
            clearInterval(pollRef.current!);
            setTranscriptStatus('error');
            setError(status.failureReason || 'Transcription failed');
          }
        } catch (err) {
          console.error('Poll error:', err);
        }
      }, 5000);
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [transcriptStatus, getS3Key]);

  const handleTranscribe = async () => {
    setTranscriptStatus('starting');
    setError(null);
    try {
      const s3Key = getS3Key();
      const result = await api.startTranscription(s3Key);
      setJobId(result.jobName);
      setTranscriptStatus('in_progress');
    } catch (err: any) {
      setTranscriptStatus('error');
      setError(err.message || 'Failed to start transcription');
    }
  };

  const handleExport = async (pattern: string) => {
    setExporting(true);
    setError(null);
    try {
      const s3Key = getS3Key();
      const data = await api.getPlaybackUrl(s3Key);
      const downloadUrl = data.url.replace('/stream/', '/download/');

      const resp = await fetch(downloadUrl);
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();

      const customName = resolvePattern(pattern, recording);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = customName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      setShowExport(false);
    } catch (err: any) {
      setError(err.message || 'Export failed');
    }
    setExporting(false);
  };

  const handleDownloadPdf = async () => {
    try {
      if (!transcript) {
        setError('No transcript to download');
        return;
      }

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - margin * 2;
      let y = 20;

      const addText = (text: string, size: number, bold: boolean = false) => {
        doc.setFontSize(size);
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        const lines = doc.splitTextToSize(text, maxWidth);
        for (const line of lines) {
          if (y > 270) {
            doc.addPage();
            y = 20;
          }
          doc.text(line, margin, y);
          y += size * 0.5;
        }
      };

      const formatTime = (s: number) => `${Math.floor(s / 60)}:${(Math.floor(s) % 60).toString().padStart(2, '0')}`;

      // Header
      addText('CALL TRANSCRIPT', 16, true);
      y += 4;
      addText(`Recording: ${recording.originalFilename || 'Unknown'}`, 9);
      addText(`System: ${recording.systemId || 'Unknown'}  |  Date: ${recording.recordDate || 'Unknown'}  |  Duration: ${transcript.duration ? formatTime(transcript.duration) : 'Unknown'}`, 9);
      addText(`Agent: ${recording.agentId || 'Unknown'}  |  Caller: ${recording.callerId || 'Unknown'}`, 9);
      addText(`Generated: ${new Date().toISOString()}`, 8);
      y += 4;

      doc.setDrawColor(180);
      doc.line(margin, y, pageWidth - margin, y);
      y += 8;

      // Segments
      for (const seg of transcript.segments) {
        const timeStr = formatTime(seg.startTime);
        addText(`[${timeStr}] ${seg.speaker}:`, 10, true);
        addText(seg.text, 10);
        y += 4;
      }

      // Footer
      y += 4;
      doc.setDrawColor(180);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;
      addText('This transcript is auto-generated and may contain inaccuracies.', 8);
      addText('Pulse Storage - Call Recording Management', 8);

      doc.save(`transcript-${recording.originalFilename?.replace(/\.[^.]+$/, '') || 'recording'}.pdf`);
    } catch (err: any) {
      setError(err.message || 'PDF generation failed');
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const speakerColor = (speaker: string) => {
    const colors: Record<string, string> = {
      spk_0: '#2196F3', spk_1: '#4CAF50', spk_2: '#FF9800', spk_3: '#9C27B0',
    };
    return colors[speaker] || '#666';
  };

  const speakerLabel = (speaker: string) => {
    const labels: Record<string, string> = {
      spk_0: 'Speaker 1', spk_1: 'Speaker 2', spk_2: 'Speaker 3', spk_3: 'Speaker 4',
    };
    return labels[speaker] || speaker;
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Recording Details</h2>
          <button onClick={onClose} style={closeButtonStyle}>Close</button>
        </div>

        {/* Metadata */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16, fontSize: 14 }}>
          <div><strong>System:</strong> {recording.systemId}</div>
          <div><strong>Date:</strong> {recording.callDate || recording.recordDate}</div>
          <div><strong>Agent:</strong> {recording.agentId || '-'}</div>
          <div><strong>Direction:</strong> {recording.direction || '-'}</div>
          {/* Full phone number when we have it, fall back to the last-4
              callerId only if callerPhone is missing. Adam: "the last 4 is
              useless to me, it should show the whole phone number". */}
          <div><strong>Caller:</strong> {recording.callerPhone || recording.callerId || '-'}</div>
          <div><strong>Duration:</strong> {formatModalDuration(recording)}</div>
          <div><strong>Size:</strong> {recording.fileSizeBytes ? formatSize(recording.fileSizeBytes) : '-'}</div>
          <div style={{ gridColumn: '1 / -1' }}><strong>File:</strong> {recording.originalFilename}</div>
        </div>

        {/* Audio Player or Archive Restore */}
        {!audioUrl && recording.storageStatus === 'glacier' && (
          <div style={{ marginBottom: 16, padding: 16, background: '#fff3e0', borderRadius: 8, border: '1px solid #ffe0b2' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 24 }}>&#128451;</span>
              <div>
                <strong>This recording is in deep archive</strong>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>
                  {restoreStatus === 'initiated' || restoreStatus === 'in_progress'
                    ? restoreMessage || 'Restore in progress. You will receive an email when ready (12-48 hours).'
                    : 'This recording is older than 3 years and has been moved to Glacier archive. Request a restore to listen to it.'}
                </p>
              </div>
            </div>
            {restoreStatus !== 'initiated' && restoreStatus !== 'in_progress' && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const s3Key = recording.s3Key || getS3Key();
                    const result = await api.requestGlacierRestore(s3Key, recording.recordingId);
                    setRestoreStatus(result.status);
                    setRestoreMessage(result.message);
                  } catch (err: any) {
                    setError(err.message || 'Restore request failed');
                  }
                }}
                style={{ ...primaryButtonStyle, marginTop: 12, background: '#e65100' }}
              >
                Request from Archive
              </button>
            )}
            {(restoreStatus === 'initiated' || restoreStatus === 'in_progress') && (
              <div style={{ marginTop: 12 }}>
                <span className="badge badge-warning">Restore Pending</span>
                <span style={{ marginLeft: 8, fontSize: 13, color: '#666' }}>Check back in 12-48 hours or wait for email notification</span>
              </div>
            )}
          </div>
        )}
        {audioUrl && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f8f9fa', borderRadius: 8 }}>
            <audio
              ref={audioRef}
              src={audioUrl}
              controls
              controlsList="noplaybackrate"
              preload="auto"
              style={{ width: '100%' }}
              onError={(e) => {
                const audio = e.currentTarget;
                setError(`Audio playback error: ${audio.error?.message || 'Unknown error'}`);
              }}
            />
          </div>
        )}

        {/* Export / Download */}
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <button
            type="button"
            onClick={() => setShowExport(!showExport)}
            disabled={exporting}
            style={{
              ...primaryButtonStyle,
              background: '#2196F3',
            }}
          >
            {exporting ? 'Exporting...' : 'Export / Download'}
          </button>
          {showExport && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              border: '1px solid #e0e0e0',
              zIndex: 10,
              minWidth: 400,
              maxHeight: 400,
              overflowY: 'auto',
            }}>
              {/* Original filename */}
              <div
                onClick={() => handleExport('{original}')}
                style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>Original Filename</div>
                <div style={{ fontSize: 11, color: '#aaa', fontFamily: 'monospace', marginTop: 2 }}>
                  {resolvePattern('{original}', recording)}
                </div>
              </div>

              {/* Saved custom formats */}
              {savedFormats.map(fmt => (
                <div
                  key={fmt.id}
                  onClick={() => handleExport(fmt.pattern)}
                  style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{fmt.name}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{fmt.pattern}</div>
                  <div style={{ fontSize: 11, color: '#aaa', fontFamily: 'monospace', marginTop: 2 }}>
                    {resolvePattern(fmt.pattern, recording)}
                  </div>
                </div>
              ))}

              {savedFormats.length === 0 && (
                <div style={{ padding: '12px 16px', color: '#999', fontSize: 13 }}>
                  No custom formats yet. Create them in Admin &rarr; Export Formats.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Transcription Controls */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          {transcriptStatus === 'idle' && (
            <button onClick={handleTranscribe} style={primaryButtonStyle}>
              Transcribe Recording
            </button>
          )}
          {transcriptStatus === 'starting' && (
            <span style={{ color: '#888' }}>Starting transcription...</span>
          )}
          {transcriptStatus === 'in_progress' && (
            <span style={{ color: '#f39c12' }}>Transcribing... (this may take 1-2 minutes)</span>
          )}
          {transcriptStatus === 'completed' && (
            <>
              <span className="badge badge-success">Transcript Ready</span>
              <button onClick={handleDownloadPdf} style={secondaryButtonStyle}>
                Download PDF
              </button>
            </>
          )}
          {transcriptStatus === 'error' && (
            <>
              <span className="badge badge-danger">Transcription Failed</span>
              <button onClick={handleTranscribe} style={primaryButtonStyle}>Retry</button>
            </>
          )}
        </div>

        {error && <div style={{ color: '#e74c3c', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {/* Transcript Content */}
        {transcript && transcript.noSpeech && (
          <div style={transcriptContainerStyle}>
            <div style={{ color: '#888', fontSize: 14, padding: '8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>🔇</span>
              No speech detected — this recording appears to be silent or contains no recognizable speech.
            </div>
          </div>
        )}

        {transcript && !transcript.noSpeech && (
          <div style={transcriptContainerStyle}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 15 }}>
              Transcript ({transcript.segments.length} segments, {transcript.speakerCount} speaker{transcript.speakerCount !== 1 ? 's' : ''})
            </h3>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {transcript.segments.map((seg, i) => (
                <div key={i} style={{ marginBottom: 12, paddingLeft: 12, borderLeft: `3px solid ${speakerColor(seg.speaker)}` }}>
                  <div style={{ fontSize: 12, color: speakerColor(seg.speaker), fontWeight: 700, marginBottom: 2 }}>
                    {speakerLabel(seg.speaker)} — {formatTime(seg.startTime)}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.5 }}>{seg.text}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function getToken(): Promise<string> {
  const { fetchAuthSession } = await import('aws-amplify/auth');
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() || '';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// Matches the formula in SystemView.tsx -- when duration is set in DDB,
// show it; otherwise fall back to a filesize-based estimate. Per-system
// divisors because Vicidial stores ~16 kbps MP3 and DialedIn ~128 kbps.
// Browsers <audio> element may report a different value (it decodes the
// file's MPEG header); the metadata grid intentionally shows our estimate
// so the list and modal numbers agree.
function formatModalDuration(r: any): string {
  const fmt = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  if (r.duration !== undefined && r.duration !== null && r.duration !== '') {
    const d = typeof r.duration === 'string' ? parseInt(r.duration) : r.duration;
    if (!isNaN(d) && d > 0) return fmt(d);
  }
  if (!r.fileSizeBytes) return '-';
  // Per-system divisor (bytes/sec). Matches SystemView's bytesPerSecForSystem
  // post-2026-05-19 correction (DialedIn is 64 kbps not 128). After the
  // mediainfo probe was restored in ingest-recordings.py + the ffprobe
  // backfill ran, every DialedIn row carries a real duration and this
  // fallback rarely fires -- but if it does, it should at least estimate
  // at the right rate.
  const bytesPerSec = r.systemId === 'vicidial' ? 2000 : r.systemId === 'dialedin' ? 8000 : 16000;
  const seconds = Math.round(r.fileSizeBytes / bytesPerSec);
  if (seconds < 1) return '-';
  return fmt(seconds);
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', zIndex: 1000,
  display: 'flex', justifyContent: 'center', alignItems: 'center',
};

const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: 24,
  maxWidth: 800, width: '90%', maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};

const closeButtonStyle: React.CSSProperties = {
  background: '#eee', border: 'none', borderRadius: 6,
  padding: '8px 16px', cursor: 'pointer', fontSize: 14,
};

const primaryButtonStyle: React.CSSProperties = {
  background: '#00bcd4', color: '#fff', border: 'none', borderRadius: 6,
  padding: '10px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 600,
};

const secondaryButtonStyle: React.CSSProperties = {
  background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6,
  padding: '10px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 600,
};

const transcriptContainerStyle: React.CSSProperties = {
  background: '#f8f9fa', borderRadius: 8, padding: 16,
};

export default RecordingDetail;
